import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const API = process.env.REACT_APP_API_URL;
const StudentComplaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openRaiseModal, setOpenRaiseModal] = useState(false);
  const [newComplaint, setNewComplaint] = useState({
    title: "",
    description: "",
    category: "library",
    screenshot: null,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [complaintToDelete, setComplaintToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchComplaints = async () => {
    const loadingToast = toast.loading("Loading complaints...");
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}complaints/`, config);
      setComplaints(res.data);
      setIsLoading(false);
      toast.dismiss(loadingToast);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to load complaints.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
  }, []);

  const handleRaiseComplaint = async () => {
    if (!newComplaint.title || !newComplaint.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    const formData = new FormData();
    formData.append("title", newComplaint.title);
    formData.append("description", newComplaint.description);
    formData.append("category", newComplaint.category);
    if (newComplaint.screenshot) {
      formData.append("screenshot", newComplaint.screenshot);
    }

    const loadingToast = toast.loading("Submitting complaint...");
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };
      await axios.post(`${API}complaints/`, formData, config);
      toast.dismiss(loadingToast);
      toast.success("✅ Complaint submitted successfully!");
      setOpenRaiseModal(false);
      setNewComplaint({
        title: "",
        description: "",
        category: "library",
        screenshot: null,
      });
      fetchComplaints();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to submit complaint.");
    }
  };

  const handleResolveComplaint = async (id) => {
    const loadingToast = toast.loading("Marking as resolved...");
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `${API}complaints/${id}/resolve/`,
        {
          title: selectedComplaint.title,
          description: selectedComplaint.description,
          category: selectedComplaint.category,
          status: selectedComplaint.status,
        },
        config
      );
      toast.dismiss(loadingToast);
      toast.success("✅ Complaint marked as resolved.");
      setOpenViewModal(false);
      fetchComplaints();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to resolve complaint.");
    }
  };

  const handleDeleteComplaint = async (id) => {
    const loadingToast = toast.loading("Removing complaint...");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_API_URL}complaints/${id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.dismiss(loadingToast);
      toast.success("✅ Complaint removed successfully.");
      setSelectedComplaint(null);
      fetchComplaints();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to remove complaint");
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      resolved: {
        icon: <CheckCircleIcon fontSize="small" />,
        color: "success",
        bgColor: "bg-green-100",
        textColor: "text-green-700",
      },
      pending: {
        icon: <PendingIcon fontSize="small" />,
        color: "warning",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-700",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        icon={config.icon}
        label={status}
        color={config.color}
        size="small"
        className={`${config.bgColor} ${config.textColor} font-medium`}
        sx={{ borderRadius: "12px" }}
      />
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      library: "📚",
      other: "📝",
    };
    return icons[category] || "📝";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-4 lg:p-6">
      <div className="max-w-full mx-auto space-y-4 md:space-y-6">
        {/* Header Section with Raise Button */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div></div>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenRaiseModal(true)}
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              },
              py: 1.5,
              px: 3,
            }}
          >
            <span className="hidden sm:inline">Raise Complaint</span>
            <span className="sm:hidden">Raise</span>
          </Button>
        </div>

        {/* Complaints Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <Typography variant="h6" className="text-gray-600">
                  Loading complaints...
                </Typography>
              </div>
            </div>
          ) : complaints.length === 0 ? (
            <div className="p-4 md:p-8 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ReportProblemIcon className="text-gray-400 text-xl md:text-2xl" />
              </div>
              <Typography variant="h6" className="text-gray-600 mb-2">
                No complaints yet
              </Typography>
              <Typography variant="body2" className="text-gray-500 mb-4">
                Start by raising your first complaint
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpenRaiseModal(true)}
                sx={{ borderRadius: "12px" }}
              >
                Raise Complaint
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile Cards View */}
              <div className="block md:hidden">
                <div className="space-y-3 p-3">
                  {complaints.map((complaint) => (
                    <div
                      key={complaint.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {getCategoryIcon(complaint.category)}
                          </span>
                          <div>
                            <div className="font-semibold text-gray-900">
                              #{complaint.id}
                            </div>
                            <div className="text-sm text-gray-500 capitalize">
                              {complaint.category}
                            </div>
                          </div>
                        </div>
                        {getStatusChip(complaint.status)}
                      </div>

                      <div className="mb-3">
                        <div className="font-medium text-gray-900 mb-1">
                          {complaint.title}
                        </div>
                        <div className="text-gray-600 text-sm line-clamp-2">
                          {complaint.description}
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <div>
                          <div>
                            {new Date(
                              complaint.submitted_on
                            ).toLocaleDateString()}
                          </div>
                          <div>
                            {new Date(
                              complaint.submitted_on
                            ).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedComplaint(complaint);
                            setOpenViewModal(true);
                          }}
                          sx={{
                            borderRadius: "8px",
                            borderColor: "#3b82f6",
                            color: "#3b82f6",
                            "&:hover": {
                              borderColor: "#2563eb",
                              backgroundColor: "rgba(59, 130, 246, 0.1)",
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                          <span className="ms-2">View</span>
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setComplaintToDelete(complaint.id);
                            setDeleteConfirmOpen(true);
                          }}
                          sx={{
                            color: "#ef4444",
                            "&:hover": {
                              backgroundColor: "rgba(239, 68, 68, 0.1)",
                            },
                          }}
                        >
                          <DeleteForeverOutlinedIcon fontSize="small" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-300 rounded-md shadow-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-red-500 to-red-600 text-white">
                        <th className="p-3 text-left font-semibold">ID</th>
                        <th className="p-3 text-left font-semibold">Title</th>
                        <th className="p-3 text-left font-semibold">
                          Category
                        </th>
                        <th className="p-3 text-left font-semibold">
                          Submitted On
                        </th>
                        <th className="p-3 text-left font-semibold">Status</th>
                        <th className="p-3 text-left font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {complaints.map((complaint) => (
                        <tr
                          key={complaint.id}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-4 py-4 font-medium">
                            #{complaint.id}
                          </td>
                          <td className="px-4 py-4">
                            <div className="max-w-[200px]">
                              <div className="font-medium text-gray-900">
                                {complaint.title}
                              </div>
                              <div className="text-gray-500 text-xs line-clamp-2">
                                {complaint.description}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {getCategoryIcon(complaint.category)}
                              </span>
                              <span className="capitalize font-medium text-gray-900">
                                {complaint.category}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-gray-600">
                              {new Date(
                                complaint.submitted_on
                              ).toLocaleDateString()}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {new Date(
                                complaint.submitted_on
                              ).toLocaleTimeString()}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {getStatusChip(complaint.status)}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex gap-2">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setSelectedComplaint(complaint);
                                  setOpenViewModal(true);
                                }}
                                sx={{
                                  borderRadius: "8px",
                                  borderColor: "#3b82f6",
                                  color: "#3b82f6",
                                  "&:hover": {
                                    borderColor: "#2563eb",
                                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="medium" />
                                <span className="ms-2">View</span>
                              </Button>
                              <Button
                                variant="text"
                                size="medium"
                                onClick={() => {
                                  setComplaintToDelete(complaint.id);
                                  setDeleteConfirmOpen(true);
                                }}
                                sx={{
                                  color: "#ef4444",
                                  "&:hover": {
                                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                                  },
                                }}
                              >
                                <DeleteForeverOutlinedIcon fontSize="medium" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: "16px",
            maxWidth: "90vw",
            width: "100%",
          },
        }}
      >
        <DialogTitle className="font-semibold text-gray-800">
          Confirm Delete
        </DialogTitle>
        <DialogContent className="py-4">
          <Typography>
            Are you sure you want to delete this complaint? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions className="p-4">
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteComplaint(complaintToDelete);
              setDeleteConfirmOpen(false);
            }}
            variant="contained"
            color="error"
            sx={{ borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Complaint Modal */}
      <Dialog
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: "16px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle className="font-semibold text-gray-800 border-b border-gray-100">
          Complaint Details
        </DialogTitle>
        <DialogContent className="py-4 md:py-6">
          {selectedComplaint && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-600 mb-1"
                  >
                    Complaint ID
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    #{selectedComplaint.id}
                  </Typography>
                </div>
                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-600 mb-1"
                  >
                    Status
                  </Typography>
                  {getStatusChip(selectedComplaint.status)}
                </div>
                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-600 mb-1"
                  >
                    Category
                  </Typography>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {getCategoryIcon(selectedComplaint.category)}
                    </span>
                    <Typography
                      variant="body1"
                      className="font-medium capitalize"
                    >
                      {selectedComplaint.category}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-600 mb-1"
                  >
                    Submitted On
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {new Date(
                      selectedComplaint.submitted_on
                    ).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500">
                    {new Date(
                      selectedComplaint.submitted_on
                    ).toLocaleTimeString()}
                  </Typography>
                </div>
              </div>

              <div>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                  Title
                </Typography>
                <Typography variant="body1" className="font-medium">
                  {selectedComplaint.title}
                </Typography>
              </div>

              <div>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                  Description
                </Typography>
                <Typography
                  variant="body1"
                  className="bg-gray-50 p-3 rounded-lg"
                >
                  {selectedComplaint.description}
                </Typography>
              </div>

              {selectedComplaint.screenshot && (
                <div>
                  <Typography
                    variant="subtitle2"
                    className="text-gray-600 mb-2"
                  >
                    Screenshot
                  </Typography>
                  <img
                    src={selectedComplaint.screenshot}
                    alt="screenshot"
                    className="w-full max-w-md rounded-lg border shadow-sm"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions className="p-4 border-t border-gray-100">
          {selectedComplaint?.status !== "resolved" && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleResolveComplaint(selectedComplaint.id)}
              sx={{ borderRadius: "8px" }}
            >
              <span className="hidden sm:inline">Mark as Resolved</span>
              <span className="sm:hidden">Resolve</span>
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => setOpenViewModal(false)}
            sx={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raise Complaint Modal */}
      <Dialog
        open={openRaiseModal}
        onClose={() => setOpenRaiseModal(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: "16px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle className="font-semibold text-gray-800 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <AddIcon className="text-blue-600" />
            Raise New Complaint
          </div>
        </DialogTitle>
        <DialogContent
          className="py-4 md:py-6 space-y-4"
          sx={{
            paddingTop: "16px !important",
            paddingBottom: "16px !important",
          }}
        >
          <TextField
            label="Complaint Title"
            fullWidth
            required
            value={newComplaint.title}
            onChange={(e) =>
              setNewComplaint({ ...newComplaint, title: e.target.value })
            }
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            required
            value={newComplaint.description}
            onChange={(e) =>
              setNewComplaint({ ...newComplaint, description: e.target.value })
            }
            placeholder="Please describe your complaint in detail..."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />

          <TextField
            label="Category"
            select
            fullWidth
            value={newComplaint.category}
            onChange={(e) =>
              setNewComplaint({ ...newComplaint, category: e.target.value })
            }
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          >
            <MenuItem value="library">📚 Library</MenuItem>
            <MenuItem value="other">📝 Other</MenuItem>
          </TextField>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 md:p-6 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              accept="image/png,image/jpg,image/jpeg"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Check file size (2MB = 2 * 1024 * 1024 bytes)
                  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
                  if (file.size > maxSize) {
                    toast.error("File size must be less than 2MB");
                    e.target.value = null; // Clear the input
                    return;
                  }
                  setNewComplaint({
                    ...newComplaint,
                    screenshot: file,
                  });
                }
              }}
              className="hidden"
              id="screenshot-upload"
            />
            <label htmlFor="screenshot-upload" className="cursor-pointer">
              <CloudUploadIcon className="text-gray-400 text-3xl md:text-4xl mb-2" />
              <Typography variant="body2" className="text-gray-600 mb-1">
                {newComplaint.screenshot
                  ? newComplaint.screenshot.name
                  : "Upload Screenshot (Optional)"}
              </Typography>
              <Typography variant="caption" className="text-gray-500">
                Click to upload an image file (Max 2MB | Accepted: PNG, JPG,
                JPEG)
              </Typography>
            </label>
          </div>
        </DialogContent>
        <DialogActions className="p-4 border-t border-gray-100">
          <Button
            onClick={() => setOpenRaiseModal(false)}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRaiseComplaint}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              },
            }}
          >
            <span className="hidden sm:inline">Submit Complaint</span>
            <span className="sm:hidden">Submit</span>
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StudentComplaints;
